/**
 * 매입관리 (Phase 7-1a) — 4탭 부모 페이지.
 *
 * 본 단계 (7-1a) 구현 범위:
 *   - 사이드바 메뉴 진입 + 탭 4개 골격
 *   - 탭1 매입 목록: 필터 + DataTable + 행 클릭=수정 모달 + 체크박스 일괄삭제 + 등록 모달
 *   - 권한:
 *      · viewer  : 단가/금액 컬럼 hidden (백엔드도 null 마스킹) + 체크박스/등록 X
 *      · sales+ admin : 풀세트 (등록·수정·삭제)
 *
 * 다음 단계:
 *   - 7-1b 주요품목 원가 (HDD/기타 좌우 표 + 행 클릭 모달)
 *   - 7-1c 연도별 매입/출고 + 주요 매입처 + 엑셀 export
 */

import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as purchaseApi from '../lib/purchase-api';
import * as prefsApi from '../lib/preferences-api';
import { resolveActiveColumns, resolveColumnOrder } from '../lib/columnPrefs';
import { SearchBar } from '../components/SearchBar';
import { ResetMenu } from '../components/ResetButton';
import { ExcelModeGroup } from '../components/ExcelModeButton';
import { YearDropdown } from '../components/YearDropdown';
import { syncAllData } from '../lib/invalidate';
import { usePersistedState } from '../lib/usePersistedState';
import type {
  CompanyId,
  KeyItemCostRow,
  PurchaseDetail,
  PurchaseListRow,
  PurchaseSupplierRow,
  PurchaseType,
  PurchaseYearlyItem,
  UserPreferences,
} from '../lib/types';
import {
  type Column,
  ColumnHeader,
  applyColState,
  useColState,
} from '../components/DataTable';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { VatNote } from '../components/VatNote';
import { NtsLastUpload } from '../components/NtsLastUpload';
import { useAuth } from '../lib/auth-context';
import { PageTabs } from '../components/ui/PageTabs';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { poPdfDownloadUrl } from '../lib/po-api';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { listAttachments, previewAttachment, PURCHASE_ATTACH_KIND } from '../lib/attachment-api';
import { AttachmentDownloadModal } from '../components/AttachmentDownloadModal';
import { LoadingLines } from '../components/ui/Skeleton';

// 모달은 lazy — 초기 번들에서 분리. 사용자가 행 클릭 / [+ 매입 등록] 시 처음 로드.
const PurchaseDetailModal = lazy(() =>
  import('../components/PurchaseDetailModal').then((m) => ({ default: m.PurchaseDetailModal })),
);
const PurchaseFormModal = lazy(() =>
  import('../components/PurchaseFormModal').then((m) => ({ default: m.PurchaseFormModal })),
);
const NtsImportModal = lazy(() =>
  import('../components/NtsImportModal').then((m) => ({ default: m.NtsImportModal })),
);
const NtsGroupModal = lazy(() =>
  import('../components/NtsGroupModal').then((m) => ({ default: m.NtsGroupModal })),
);
const NtsCheckModal = lazy(() =>
  import('../components/NtsCheckModal').then((m) => ({ default: m.NtsCheckModal })),
);
// 건명·매출일련번호 칸 클릭 → 그 영업건 정보 (보기 전용, v2.78.0)
const SalesSummaryModal = lazy(() =>
  import('../components/SalesSummaryModal').then((m) => ({ default: m.SalesSummaryModal })),
);

// 2026-07-19 메뉴 재구성: 수출입 탭은 독립 메뉴(/imports, 경영+ 전용)로 승격.
type PurchaseTab = 'list' | 'cost' | 'yearly' | 'supplier';

type PurchaseStage = '주문' | '입고' | '출고' | '발행';
const ALL_PURCHASE_STAGES: PurchaseStage[] = ['주문', '입고', '출고', '발행'];
const ALL_COMPANIES: CompanyId[] = ['SM', 'EJ'];
// ⚠ 여기 빠진 유형은 필터 토글이 안 생겨 목록에서 **영원히 걸러진다**(아래 activeTypes 필터).
//   백엔드 TYPE_ENUM·mapListRow·types.ts PurchaseType 과 항상 같이 늘릴 것.
const ALL_TYPES: PurchaseType[] = ['영업연동', '재고보유', '유지보수', '판관비', '기타', '제조'];
// 첫 진입 기본 선택 유형 — 판관비·제조는 제외. 사용자가 필요 시 켤 수 있음.
//   제조가 기본 꺼짐인 이유: 완제품 행의 금액은 원재료 매입액을 롤업한 값이라, 켜 두면
//   하단 매입 합계에 같은 돈이 두 번 잡힌다. 필요할 때만 켜서 보게 한다.
//   ⚠ 2026-09-12 에 기타를 기본 켜짐으로 옮겼다 — 출고는 됐지만 영업건도 장애처리도 아닌 줄
//   (사은품·사내 사용·제조 조립·재고 실사 정리)을 [기타] 로 정리하기 시작했기 때문이다.
//   꺼 두면 그 줄들이 첫 화면에서 통째로 사라지고 매입 합계도 그만큼 어긋나 보인다.
const DEFAULT_TYPES: PurchaseType[] = ['영업연동', '재고보유', '유지보수', '기타'];

// ── 매입 목록 점진 렌더링 ────────────────────────────────
// 매입은 수천 건 → 한 번에 다 그리면 느리고 가끔 멈춤(하얀화면). 처음엔 화면+버퍼만
// 그리고, 아래로 스크롤하면 묶음 단위로 더 그림. (필터·정렬·검색 변경 시 처음부터)
// 합계·검색후보·엑셀·전체선택은 그대로 '필터 후 전체'를 사용 → 숫자/내보내기 영향 없음.
const PURCHASE_INITIAL_RENDER = 60;
const PURCHASE_RENDER_STEP = 60;
// 스크롤이 바닥에서 이 거리(px) 안에 들어오면 다음 묶음을 미리 추가.
const PURCHASE_LOAD_AHEAD_PX = 800;

const TYPE_BADGE: Record<PurchaseType, string> = {
  영업연동: 'stage-badge stage-order',
  재고보유: 'stage-badge stage-quote',
  유지보수: 'stage-badge stage-deliver',
  판관비: 'stage-badge bg-slate-200 text-slate-700',
  기타: 'stage-badge bg-amber-100 text-amber-800',
  // 제조처리 산출물 (2026-07-27) — 사 온 게 아니라 자체 조립으로 생긴 재고
  제조: 'stage-badge bg-indigo-100 text-indigo-800',
};
/**
 * 줄 바탕 = **영업연동만** 파랑 (v2.320.0 · 영업진행 표(v2.319.1)와 같은 식 — 짝수 줄 조금 진하게 · 마우스 올리면 더 진하게).
 *   처음에는 여섯 유형에 전부 색을 넣었는데(영업연동 파랑·재고보유 초록·유지보수 보라·기타 노랑·판관비 회색·제조 남색) 개발 화면을 본 사용자가
 *   「영업 연동만 배경색 넣어주고, 재고는 색 없이」(11-02) — 재고보유를 비롯한 나머지는 Salesforce 식 흰 바탕 + 회색 호버(표 B).
 *   체크로 고른 줄은 유형과 상관없이 진한 파랑(종전 bg-blue-50 은 영업연동 바탕과 겹쳐 안 보인다).
 *   「유형」 칸 꼬리표는 그대로(바탕색과 꼬리표가 같은 것을 두 번 말한다).
 */
const ROW_BG_BY_TYPE: Partial<Record<PurchaseType, string>> = {
  영업연동: 'bg-blue-50 even:bg-blue-100/40 hover:bg-blue-200',
};
const ROW_BG_SELECTED = 'bg-blue-200 hover:bg-blue-200';
const ROW_BG_DEFAULT = 'hover:bg-slate-50';

const STAGE_HAS: Record<PurchaseStage, (r: PurchaseListRow) => boolean> = {
  주문: (r) => !!r.orderDate,
  입고: (r) => !!r.actualInDate,
  출고: (r) => !!r.actualOutDate,
  발행: (r) => !!r.purchaseInvoiceDate,
};

/** 매입 4단계 (발주/입고/발행/출고) 인라인 배지 — 활성 시 색, 비활성 시 회색 */
function StageBadge({
  label,
  active,
  color,
}: {
  label: string;
  active: boolean;
  color?: 'emerald' | 'blue' | 'amber';
}) {
  if (!active) {
    return (
      <span className="px-1 py-px rounded bg-slate-100 text-slate-300">
        {label}
      </span>
    );
  }
  const cls =
    color === 'emerald' ? 'bg-emerald-100 text-emerald-700'
    : color === 'blue' ? 'bg-blue-100 text-blue-700'
    : color === 'amber' ? 'bg-amber-100 text-amber-700'
    : 'bg-slate-200 text-slate-700';
  return <span className={`px-1 py-px rounded ${cls}`}>{label}</span>;
}


function fmtKrw(n: number | null): string {
  if (n === null || n === undefined) return '-';
  // 단가에 소수 저장 가능(=수식 ÷ 계산) — 표시는 항상 정수 반올림
  return Math.round(n).toLocaleString('ko-KR');
}

/** 칸별 '자동입력' 작은 마크 — 값이 시스템 자동입력값이고 아직 사람이 안 고쳤을 때 표시.
 *  사람이 그 칸을 직접 수정하면 백엔드 autoFlags 가 false → 마크 사라짐. */
function AutoMark() {
  return (
    <span
      className="ml-0.5 text-[11px] text-sky-500 align-top cursor-help select-none"
      title="자동으로 입력된 값입니다 — 직접 수정하면 마크가 사라집니다"
    >
      ✦
    </span>
  );
}

export function PurchaseListPage() {
  const [tab, setTab] = useState<PurchaseTab>('list');
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const canExport = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  // 탭3·4 의 분석 연도 (기본 현재년)
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [excelDownloading, setExcelDownloading] = useState(false);
  // 탭1 매입 목록의 클라이언트 측 exporter — 컬럼 토글 반영을 위해 PurchaseListTab 에서 등록.
  // 탭2~4 (분석) 은 서버 4시트 export 그대로 유지.
  const listExporterRef = useRef<(() => Promise<void>) | null>(null);
  // 탭1 매입 목록의 필터 후 합계 — 헤더 [📥 엑셀] 옆에 표시 (비상벨 패턴).
  const [listTotals, setListTotals] = useState<{ count: number; qty: number; amt: number } | null>(null);

  async function handleExcel() {
    if (tab === 'list') {
      const fn = listExporterRef.current;
      if (!fn) return;
      setExcelDownloading(true);
      try {
        await fn();
      } catch (e) {
        alert(`엑셀 다운로드 실패: ${(e as Error).message}`);
      } finally {
        setExcelDownloading(false);
      }
      return;
    }
    setExcelDownloading(true);
    try {
      await purchaseApi.downloadPurchaseExcel(year);
    } catch (e) {
      alert(`엑셀 다운로드 실패: ${(e as Error).message}`);
    } finally {
      setExcelDownloading(false);
    }
  }

  return (
    <div className="p-8">
      {/* 헤더 */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="ui-page-title">매입관리</h1>
          <p className="ui-page-sub flex items-center gap-2 flex-wrap">
            <span>
              {tab === 'list' && '매입 목록 — 영업연동 / 재고보유 통합'}
              {tab === 'cost' && '주요품목 원가 — 재고관리 주요재고현황 항목별 매입가'}
              {tab === 'yearly' && `연도별 매입/출고 현황 — 주요재고현황 품목 기준 (${year - 2}~${year})`}
              {tab === 'supplier' && `주요 매입처 현황 — 상위 매입처, 최근 3년 (${year - 2}~${year})`}
            </span>
            <VatNote />
          </p>
          <LastUpdateBadge className="mt-1" />
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          {tab === 'list' && listTotals && (
            <div className="text-sm font-semibold text-slate-700 pb-1.5">
              {listTotals.count.toLocaleString('ko-KR')}건
              <span className="text-slate-400 font-normal"> · </span>
              금액 <span className="text-blue-700">
                {isViewer ? '***' : `₩${listTotals.amt.toLocaleString('ko-KR')}`}
              </span>
            </div>
          )}
          {/* 탭3·4 의 연도 셀렉트 */}
          {(tab === 'yearly' || tab === 'supplier') && (
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="ui-input font-bold"
            >
              {[year + 1, new Date().getFullYear(), new Date().getFullYear() - 1].map((y) => (
                <option key={y} value={y}>
                  {y}년 기준
                </option>
              ))}
            </select>
          )}
          {canExport && (
            <button
              type="button"
              onClick={handleExcel}
              disabled={excelDownloading}
              title={
                tab === 'list'
                  ? '현재 화면에 보이는 컬럼·행 그대로 엑셀 다운로드'
                  : '매입 분석 4시트 엑셀 다운로드'
              }
              className={`btn ${excelDownloading ? 'btn-secondary' : 'btn-secondary'}`}
            >
              {excelDownloading ? '다운로드 중…' : '📥 엑셀'}
            </button>
          )}
        </div>
      </div>

      {/* 탭 4개 */}
      <PageTabs
        tabs={[
          { key: 'list', label: '매입 목록' },
          { key: 'cost', label: '주요품목 원가' },
          { key: 'yearly', label: '연도별 매입/출고' },
          { key: 'supplier', label: '주요 매입처 현황' },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel="매입관리 보기"
        className="mb-3"
      />

      {tab === 'list' && (
        <PurchaseListTab exporterRef={listExporterRef} onTotalsChange={setListTotals} />
      )}
      {tab === 'cost' && <KeyCostTab />}
      {tab === 'yearly' && <YearlyTab year={year} />}
      {tab === 'supplier' && <SupplierTab year={year} isViewer={isViewer} />}
    </div>
  );
}



// ========================================================================
// 탭1 — 매입 목록
// ========================================================================
function PurchaseListTab({
  exporterRef,
  onTotalsChange,
}: {
  exporterRef: MutableRefObject<(() => Promise<void>) | null>;
  onTotalsChange?: (totals: { count: number; qty: number; amt: number }) => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  // 국세청 매입계산서 업로드 — 관리자급(mgmt)·관리자
  const canImport = user?.role === 'mgmt' || user?.role === 'admin';

  // 매입 필터 — 영업진행과 동일 패턴.
  // 다중 토글: 진행단계 (주문/입고/출고/발행, OR 합집합), 사업자 (SM/EJ), 유형 (영업연동/재고보유).
  // 연도 (입고일 기준) — 첫 진입 default = 최근 4개년, years 응답 도착 시 운영 최근 4개로 보정.
  const [q, setQ] = useState('');
  const [activeStages, setActiveStages] = useState<Set<PurchaseStage>>(
    () => new Set(ALL_PURCHASE_STAGES),
  );
  const [activeCompanies, setActiveCompanies] = useState<Set<CompanyId>>(
    () => new Set(ALL_COMPANIES),
  );
  const [activeTypes, setActiveTypes] = useState<Set<PurchaseType>>(
    () => new Set(DEFAULT_TYPES),
  );
  // 확인필요(국세청 자동등록 대기)만 보기
  const [reviewOnly, setReviewOnly] = useState(false);
  // 📦 보유 0 숨기기 (2026-09-12) — 입출고가 끝나 [보유] 가 0 인 줄(출고 완료·서비스)을 감춘다.
  //   일회성 품목이 목록에 누적되는 것을 줄이려는 사용자 요청. 아직 입고 전인 발주 줄(보유 0 이지만
  //   진행 중)은 숨기지 않는다. 탭이 살아 있는 동안 기억(sessionStorage) — 메뉴를 오가도 유지.
  const [hideZeroStock, setHideZeroStock] = usePersistedState<boolean>('purchase-list/hideZeroStock', false);
  const [selectedYears, setSelectedYears] = useState<Set<string>>(() => {
    const cy = new Date().getFullYear();
    return new Set([String(cy), String(cy - 1), String(cy - 2), String(cy - 3)]);
  });
  const yearsInitializedRef = useRef(false);

  const yearsQuery = useQuery({
    queryKey: ['purchases', 'years'],
    queryFn: () => purchaseApi.listPurchaseYears(),
    staleTime: 5 * 60 * 1000,
  });
  const availableYears = yearsQuery.data ?? [];

  // years 응답 도착하면 1회 운영 최근 4개로 보정 (영업진행 동일 패턴)
  useEffect(() => {
    if (!yearsInitializedRef.current && availableYears.length > 0) {
      setSelectedYears(new Set(availableYears.slice(0, 4)));
      yearsInitializedRef.current = true;
    }
  }, [availableYears]);

  function toggleYear(y: string) {
    setSelectedYears((prev) => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y);
      else next.add(y);
      return next;
    });
  }
  function toggleStage(s: PurchaseStage) {
    setActiveStages((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }
  function toggleCompany(c: CompanyId) {
    setActiveCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }
  function toggleType(t: PurchaseType) {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }
  function resetToDefault() {
    setQ('');
    setActiveStages(new Set(ALL_PURCHASE_STAGES));
    setActiveCompanies(new Set(ALL_COMPANIES));
    setActiveTypes(new Set(ALL_TYPES));
    setReviewOnly(false);
    setHideZeroStock(false);
    if (availableYears.length > 0) {
      setSelectedYears(new Set(availableYears.slice(0, 4)));
    } else {
      const cy = new Date().getFullYear();
      setSelectedYears(new Set([String(cy), String(cy - 1), String(cy - 2), String(cy - 3)]));
    }
    colState.resetAll();
  }
  function resetAllOff() {
    setQ('');
    setActiveStages(new Set(ALL_PURCHASE_STAGES));
    setActiveCompanies(new Set(ALL_COMPANIES));
    setActiveTypes(new Set(ALL_TYPES));
    setHideZeroStock(false);
    setSelectedYears(new Set());  // 빈 = 전체 (미입고 포함)
    colState.resetAll();
  }

  // 모달
  const [historyTarget, setHistoryTarget] = useState<{
    itemCode: string | null;
    itemName: string | null;
    displayName: string;
    description: string | null;
  } | null>(null);
  const [creating, setCreating] = useState(false);
  // 건명·매출일련번호 칸 클릭 → 영업건 요약 (보기 전용, v2.78.0)
  const [summarySalesId, setSummarySalesId] = useState<string | null>(null);
  // 📄 발주서 PDF — 「발주서」 칸 아이콘을 눌렀을 때 (v2.287.0 미리보기 창 → v2.293.0 PDF 창 바로 · 거래 문서함 [📄 원본 PDF 보기]와 같은 이름)
  const openPoPdf = useCallback((poId: number, poNo: string) => {
    openServerPdfPreview({ pdfUrl: poPdfDownloadUrl(poId), filename: `발주서_${poNo}.pdf` });
  }, []);
  // 📎 매입 증빙(v2.308.0 · 거래명세서·주문내역 캡처) — 「발주서」 칸 📎: 하나면 바로 크게 보기 · 여럿이면 고르는 창
  const [evidenceFor, setEvidenceFor] = useState<number | null>(null);
  const openEvidence = useCallback(async (purchaseId: number) => {
    try {
      const metas = await listAttachments('purchase', String(purchaseId), PURCHASE_ATTACH_KIND);
      if (metas.length === 1) previewAttachment(metas[0]!);
      else if (metas.length > 1) setEvidenceFor(purchaseId);
    } catch (e) {
      window.alert(`증빙을 열지 못했습니다: ${(e as Error).message}`);
    }
  }, []);
  const [ntsOpen, setNtsOpen] = useState(false);
  // 국세청 매칭 점검 — 의심 매칭(발행일 불일치 등) 모아보기
  const [checkOpen, setCheckOpen] = useState(false);
  // 계산서번호(승인번호) 클릭 → 같은 계산서로 묶인 매입/비상벨 모아보기
  const [ntsGroupNo, setNtsGroupNo] = useState<string | null>(null);
  // 수정: list 응답에서 모달 전용 컬럼이 빠져있으므로 ✎ 클릭 시 detail fetch 후 모달 오픈.
  const [editingId, setEditingId] = useState<number | null>(null);
  const editingDetailQuery = useQuery({
    queryKey: ['purchaseDetail', editingId],
    queryFn: () => purchaseApi.getPurchaseDetail(editingId!),
    enabled: editingId !== null,
    staleTime: 0,
  });
  const editingDetail: PurchaseDetail | null = editingDetailQuery.data ?? null;

  // 정렬·필터 — 같은 세션 내 유지 + 초기 정렬은 발주일 desc (사용자 요청).
  const colState = useColState({
    storageKey: 'purchase-list',
    initialSort: [{ key: 'orderDate', dir: 'desc' }],
  });

  // ── 📐 내 표 설정 prefs — viewer 도 사용 가능 (VIEWER_HIDDEN_KEYS 컬럼은 풀세트 자체에서 제외) ──
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: prefsApi.getPreferences,
    staleTime: 5 * 60 * 1000,
  });
  const patchPrefs = useMutation({
    mutationFn: (next: Partial<UserPreferences>) => prefsApi.patchPreferences(next),
    onSuccess: (newPrefs) => qc.setQueryData(['preferences'], newPrefs),
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);

  // 백엔드 query 매개변수 — 토글 전부 켜져 있으면 undefined 로 미전달.
  const yearArr = useMemo<number[] | undefined>(() => {
    if (selectedYears.size === 0) return undefined;
    return [...selectedYears].map((y) => Number(y)).filter((n) => Number.isFinite(n));
  }, [selectedYears]);

  const allCompaniesOn = activeCompanies.size === ALL_COMPANIES.length;
  const allCompaniesOff = activeCompanies.size === 0;
  const companyFilter: CompanyId | undefined =
    allCompaniesOn || allCompaniesOff ? undefined : [...activeCompanies][0]!;

  const allTypesOn = activeTypes.size === ALL_TYPES.length;
  const allTypesOff = activeTypes.size === 0;
  // 유형은 4종 다중 선택이라 서버의 단일 type 파라미터로는 부분선택(2~3종)을 표현할 수 없음
  // (예전: 첫 1종만 전달 → 판관비만 끄면 영업연동만 남던 버그). → 아래 baseRows 에서 client-side 다중 필터.

  const allStagesOn = activeStages.size === ALL_PURCHASE_STAGES.length;
  const allStagesOff = activeStages.size === 0;

  const query = useQuery({
    queryKey: ['purchases', { q, company: companyFilter, years: yearArr ? [...yearArr].sort() : null, reviewOnly }],
    queryFn: () =>
      purchaseApi.listPurchases({
        pageSize: 5000,
        q: q || undefined,
        companyId: companyFilter,
        year: yearArr,
        reviewOnly: reviewOnly || undefined,
      }),
    placeholderData: keepPreviousData,
    // 사업자/유형 0개씩 = 빈 결과. 호출 자체 차단. (⚠ 확인필요만은 유형 필터를 안 본다 — 아래 baseRows)
    enabled: !allCompaniesOff && (reviewOnly || !allTypesOff),
  });

  const baseRowsRaw = query.data?.data ?? [];

  // 진행단계 client-side 필터 — 사용자 정의:
  //   - 4개 모두 ON: 전체 표시 (특례, ResetMenu 의 "기본 설정 = 전체" 의미)
  //   - 0개 ON     : 빈 표
  //   - 부분 선택   : 활성 stages 집합이 선택한 집합과 정확히 일치하는 행만
  //                  예: 주문만 ON → 주문만 있고 입고/출고/발행 없는 행
  const { rows: baseRows, hiddenZeroCount } = useMemo<{ rows: PurchaseListRow[]; hiddenZeroCount: number }>(() => {
    // ① 유형 필터 (다중 — activeTypes 에 포함된 유형만). 서버 단일 type 한계를 client 에서 정확히 처리.
    //   ⚠ 확인필요만(v2.311.0 · #24)은 유형 필터를 건너뛴다 — 국세청 확인필요 줄은 판관비라, 기본 유형(판관비 꺼짐)으로
    //   누르면 아무것도 안 보여 「판관비」를 따로 켜야 했다. 확인할 줄을 모으는 단추라 유형과 상관없이 다 보인다.
    if (allTypesOff && !reviewOnly) return { rows: [], hiddenZeroCount: 0 };
    let rows = allTypesOn || reviewOnly ? baseRowsRaw : baseRowsRaw.filter((r) => activeTypes.has(r.type as PurchaseType));
    // ② 진행단계 필터 — 활성 stages 집합이 행의 단계 집합과 정확히 일치하는 행만
    if (allStagesOff) return { rows: [], hiddenZeroCount: 0 };
    if (!allStagesOn) {
      rows = rows.filter((r) => {
        for (const s of ALL_PURCHASE_STAGES) {
          const has = STAGE_HAS[s](r);
          if (activeStages.has(s) !== has) return false;
        }
        return true;
      });
    }
    // ③ 📦 보유 0 숨기기 — [보유] 칸이 0 이고 물건이 이미 들어왔거나 나간 줄만 감춘다.
    //   (remainingQty 는 서버 계산: 입고됨·미출고·비서비스 → 수량, 그 외 0.)
    //   발주만 하고 아직 입고 전인 줄은 보유 0 이어도 진행 중이라 그대로 보인다.
    let hiddenZeroCount = 0;
    if (hideZeroStock) {
      const before = rows.length;
      rows = rows.filter((r) => !(r.remainingQty === 0 && (r.actualInDate || r.actualOutDate)));
      hiddenZeroCount = before - rows.length;
    }
    return { rows, hiddenZeroCount };
  }, [baseRowsRaw, activeTypes, allTypesOn, allTypesOff, reviewOnly, activeStages, allStagesOn, allStagesOff, hideZeroStock]);

  // 컬럼 (viewer 는 단가/금액 hidden). 수정=행 클릭, 삭제=체크박스 일괄삭제 (별도 작업 칼럼 없음).
  // defaultOn=true → 토글 prefs 가 비어있을 때 활성. viewer 는 토글 메뉴 X (전체가 default).
  // toggleLabel 은 표시 컬럼 메뉴에서 사용 (label 과 다르게 쓰고 싶을 때만).
  type PurchaseColumn = Column<PurchaseListRow> & {
    defaultOn: boolean;
    toggleLabel?: string;
  };

  const COLUMNS: PurchaseColumn[] = useMemo(() => {
    const cols: PurchaseColumn[] = [
      // 기본 정렬 키 (orderDate desc) — 사용자 요청으로 맨 왼쪽.
      {
        key: 'orderDate',
        label: '발주일',
        type: 'date',
        accessor: (r) => r.orderDate ?? '',
        render: (r) => <span className="text-slate-500">{r.orderDate ?? '-'}</span>,
        defaultOn: true,
      },
      {
        key: 'type',
        label: '유형',
        type: 'text',
        // v2.320.0 (사용자 「유형 부분 정렬이 안 되어서 — 왼쪽 정렬 · 상태 아이콘이 균일하게」): 가운데 정렬을 빼고 왼쪽에 붙인다.
        //   꼬리표는 글자 수(영업연동 4자 · 기타 2자)에 따라 폭이 달라 옆 아이콘이 들쭉날쭉했다 → 꼬리표 폭을 같게(4자 실측 60px)
        //   하고 아이콘(🔗·📦·🚢)은 그 뒤 고정 칸에 — 줄마다 같은 자리에 선다.
        // 「자동」(판관비 자동 확정)은 그림에만 — 값에 붙이면 칸 거르기(판관비)에서 자동 줄이 영영 빠진다(독립 검토 10-29)
        accessor: (r) => (r.reviewStatus === '확인필요' ? `${r.type} 확인필요` : r.type),
        render: (r) => (
          <span className="inline-flex flex-col items-start gap-0.5">
            <span className="inline-flex items-center gap-1">
              <span className={`${TYPE_BADGE[r.type]} min-w-[60px] text-center`}>{r.type}</span>
              {/* 자동 입력·수출입 연결 표시 — 줄 늘어나지 않게 유형 옆 고정 칸 하나(없으면 빈 칸). 설명은 호버 툴팁. */}
              <span className="inline-flex w-4 justify-center text-xs leading-none">
                {r.autoSource === 'subcontract' && (
                  <span className="cursor-help" title="🔗 자동으로 입력된 건입니다 — 유지보수 하도급 계약에서 자동 등록">🔗</span>
                )}
                {r.autoSource === 'release' && (
                  <span className="cursor-help" title="📦 자동으로 입력된 건입니다 — 재고관리 출고등록에서 자동 생성">📦</span>
                )}
                {/* 🚢 수출입 오더 라인 (설계안 B-②) — 지우거나 고치기 전에 연결 사실을 알린다 */}
                {r.importOrderRef && (
                  <span className="cursor-help" title={`🚢 수출입 오더 ${r.importOrderRef} 의 라인입니다 — 삭제하면 오더에서도 사라지고, 내용 수정은 수출입 화면과 같은 데이터입니다`}>🚢</span>
                )}
              </span>
            </span>
            {r.reviewStatus === '확인필요' && (
              <span className="text-[11px] px-1 rounded bg-amber-100 text-amber-700"
                title={r.ntsApprovalNo ? '국세청 자동등록 — 관리자 확인 대기' : '자동 등록(구입지급결의서 등) — 품목 등 확인 대기'}>⚠ 확인필요</span>
            )}
            {/* 판관비 자동 확정 (v2.311.0 · #24) — ⚠가 없어 눈에 덜 띄므로 작게라도 표시 */}
            {r.ntsAutoSga && r.reviewStatus !== '확인필요' && (
              <span className="text-[11px] px-1 rounded bg-green-50 text-green-700 cursor-help"
                title="국세청 계산서 — 같은 공급자 계산서가 3달 넘게 판관비로 확인돼 확인필요 없이 판관비로 들어왔습니다. 판관비가 아니면 수정 창에서 유형을 바꾸거나 [🔗 계산서 매칭 바꾸기]로 고치세요(고치면 이 공급자는 다음부터 자동에서 빠집니다).">자동</span>
            )}
          </span>
        ),
        defaultOn: true,
      },
      {
        key: 'supplier',
        label: '매입처',
        type: 'text',
        accessor: (r) => r.supplier ?? '',
        // 한글 약 10자 너비 cap — 그 이상은 ... (호버 title 로 전체 확인)
        render: (r) => (
          <div className="max-w-[140px] truncate" title={r.supplier ?? ''}>
            {r.supplier ?? ''}
          </div>
        ),
        defaultOn: true,
      },
      {
        // 📄 우리가 매입처에 보낸 발주서(v2.287.0).
        // v2.293.0 (2026-10-23 사용자 「발주서 이름은 지우고 다운받을 수 있는 아이콘만 · 칼럼 너비 최소화」): 번호 글자를 빼고 📄 하나 —
        //   누르면 발주서 PDF 창(⬇ 저장 · 옛 발주서는 원본 PDF 그대로). 번호는 마우스를 올리면 · 머리줄 거르기·찾기는 전체 번호로 그대로.
        //   조회 전용은 PDF 통로가 막혀 있어(sales 이상) 흐린 아이콘만. 「이 발주서로 등록된 매입」은 거래 문서함 [발주서]에서.
        key: 'poNo',
        label: '발주서',
        type: 'text',
        nowrapHeader: true,
        align: 'center',
        // v2.308.0: 📎 매입 증빙(거래명세서·쇼핑몰 주문내역 캡처)도 이 칸에 — 발주서 없는 매입은 「📎 증빙」으로 거르고 찾는다
        accessor: (r) => r.poNo ?? ((r.evidenceCount ?? 0) > 0 ? '📎 증빙' : ''),
        render: (r) => {
          const ev = r.evidenceCount ?? 0;
          const po = r.poNo
            ? (canEdit && r.poId != null
              ? <button type="button" onClick={(e) => { e.stopPropagation(); openPoPdf(r.poId!, r.poNo!); }} className="text-sm leading-none hover:opacity-70" title={`발주서 ${r.poNo} — 누르면 PDF 보기·저장`} aria-label={`발주서 ${r.poNo} PDF`}>📄</button>
              : <span className="text-sm leading-none opacity-50 cursor-default" title={`발주서 ${r.poNo}`}>📄</span>)
            : null;
          const evi = ev > 0
            ? (canEdit
              ? <button type="button" onClick={(e) => { e.stopPropagation(); void openEvidence(r.purchaseId); }} className="text-sm leading-none hover:opacity-70 whitespace-nowrap" title={`매입 증빙 ${ev}개(거래명세서·캡처) — 누르면 보기`} aria-label={`매입 증빙 ${ev}개`} data-evidence-btn>📎{ev > 1 ? <span className="text-[11px] align-top text-slate-500">{ev}</span> : null}</button>
              : <span className="text-sm leading-none opacity-50 cursor-default" title={`매입 증빙 ${ev}개`}>📎</span>)
            : null;
          if (!po && !evi) return <span className="text-slate-300">-</span>;
          return <span className="inline-flex items-center gap-1">{po}{evi}</span>;
        },
        defaultOn: true,
      },
      {
        key: 'itemName',
        label: '품목',
        type: 'text',
        accessor: (r) => r.itemName ?? r.description ?? '',
        // 한글 약 20자 너비 cap — 그 이상은 ...
        // (작은 글씨 description 은 행 클릭 시 열리는 상세 모달 헤더에서만 표시)
        render: (r) => {
          const text = r.itemName ?? r.description ?? '-';
          return (
            <div className="max-w-[280px] truncate" title={text}>
              {text}
            </div>
          );
        },
        defaultOn: true,
      },
      {
        key: 'spec',
        label: '규격',
        type: 'text',
        // 표시 우선순위: 매입 행 description(개별 작성) > item_master.spec(재고관리 기본값).
        // 단 국세청 자동등록(item_code 없음)은 description 이 '품목'이므로, 규격엔
        //   국세청 자료의 규격(purchase.spec)만 표시 — 품목·규격 칼럼 중복 방지.
        accessor: (r) => (r.itemCode ? (r.description?.trim() ? r.description : r.spec ?? '') : (r.spec ?? '')),
        render: (r) => {
          const text = r.itemCode ? (r.description?.trim() ? r.description : r.spec) : r.spec;
          if (!text) return <span className="text-slate-300">-</span>;
          return (
            <div className="max-w-[200px] truncate text-xs text-slate-700" title={text}>
              {text}
            </div>
          );
        },
        defaultOn: true,
      },
      {
        key: 'orderQty',
        label: '수량',
        type: 'number',
        align: 'right',
        accessor: (r) => r.orderQty,
        render: (r) => <span className="num">{r.orderQty}</span>,
        defaultOn: true,
      },
      {
        // 보유수량 = 입고됐지만 아직 출고되지 않은 수량 (재고로 남아있는 수량). 출고·미입고면 0.
        key: 'remainingQty',
        label: '보유',
        type: 'number',
        align: 'right',
        accessor: (r) => r.remainingQty,
        render: (r) => (
          <span className={`num ${r.remainingQty > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-300'}`}>
            {r.remainingQty}
          </span>
        ),
        defaultOn: true,
      },
    ];

    cols.push(
      {
        key: 'unitPrice',
        label: '단가',
        type: 'number',
        align: 'right',
        accessor: (r) => r.unitPrice ?? 0,
        render: (r) => <span className="num">{fmtKrw(r.unitPrice)}</span>,
        defaultOn: true,
      },
      {
        key: 'amount',
        label: '금액',
        type: 'number',
        align: 'right',
        accessor: (r) => r.amount ?? 0,
        render: (r) => (
          <span className="num font-semibold">{fmtKrw(r.amount)}</span>
        ),
        defaultOn: true,
      },
    );

    cols.push(
      {
        key: 'salesId',
        label: '매출일련번호',
        type: 'text',
        accessor: (r) => r.salesId ?? '',
        render: (r) =>
          r.salesId ? (
            // 클릭 → 그 영업건 정보 (보기 전용). 행 클릭(매입 수정)과 겹치므로 전파 차단.
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSummarySalesId(r.salesId!);
              }}
              title="클릭 → 이 영업건 정보 보기"
              className="text-blue-700 font-mono text-xs underline decoration-dotted underline-offset-2 hover:decoration-solid"
            >
              {r.salesId}
            </button>
          ) : (
            <span className="text-slate-400 text-xs">-</span>
          ),
        defaultOn: false,
      },
      {
        key: 'stages',
        label: '진행',
        type: 'text',
        // 정렬·필터용 키 — 활성 단계 이름을 ' · ' join 한 텍스트.
        //   예: "주문 · 입고", "주문 · 입고 · 발행", "미진행".
        //   컬럼 헤더 ▼ 필터 메뉴에 사람이 읽을 수 있게 표시됨.
        accessor: (r) => {
          const stages: string[] = [];
          if (r.orderDate) stages.push('주문');
          if (r.actualInDate) stages.push('입고');
          if (r.actualOutDate) stages.push('출고');
          if (r.purchaseInvoiceDate) stages.push('발행');
          return stages.length > 0 ? stages.join(' · ') : '미진행';
        },
        // 정렬 시 진행도 (완료 단계 개수) 기준 — "미진행" 0개 → "모두" 4개 순.
        sortAccessor: (r) =>
          (r.orderDate ? 1 : 0) +
          (r.actualInDate ? 1 : 0) +
          (r.actualOutDate ? 1 : 0) +
          (r.purchaseInvoiceDate ? 1 : 0),
        // ⭐ 출고 배지 = 실제 출고일(actual_out_date) 유무만 (2026-07-27).
        //   종전엔 연결 영업이 납품/발행/판보면 출고일이 없어도 '출고*'(주황)로 켜고 ⓘ 로
        //   "v_stock 에서 자동 출고 처리됨" 이라 안내했는데, 이는 2026-07-02 v_stock 개편
        //   (입고된 재고는 실제 출고일이 있어야만 출고) 이전의 옛 기준이 남은 것이다.
        //   실제로는 창고에 그대로 있는 물건이 '출고됨'으로 보였고, 같은 컬럼의 정렬·필터
        //   (accessor·STAGE_HAS.출고)는 출고일 기준이라 배지와 필터가 서로 어긋났다.
        //   (v2.003 매입관리 [보유]·v2.004 가용재고와 같은 계열의 잔존 옛 규칙)
        render: (r) => (
          <div className="flex gap-0.5 text-[11px] font-semibold">
            <StageBadge label="주문" active={!!r.orderDate} />
            <StageBadge label="입고" active={!!r.actualInDate} color="emerald" />
            <StageBadge label="출고" active={!!r.actualOutDate} color="amber" />
            <StageBadge label="발행" active={!!r.purchaseInvoiceDate} color="blue" />
          </div>
        ),
        defaultOn: true,
      },
      {
        key: 'actualInDate',
        label: '입고일',
        type: 'date',
        accessor: (r) => r.actualInDate ?? '',
        render: (r) => <span className="text-slate-500">{r.actualInDate ?? '-'}</span>,
        defaultOn: true,
      },
      {
        key: 'purchaseInvoiceDate',
        label: '매입 발행일',
        type: 'date',
        accessor: (r) => r.purchaseInvoiceDate ?? '',
        render: (r) => {
          // 국세청 매칭 건 — 발행일 클릭 시 그 승인번호(계산서 1장) 내역 팝업.
          if (r.purchaseInvoiceDate && r.ntsApprovalNo) {
            const no = r.ntsApprovalNo;
            return (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setNtsGroupNo(no); }}
                className="group text-slate-600 hover:text-blue-700"
                title={`국세청 계산서 내역 보기 (승인번호 …${no.slice(-8)})`}
              >
                <span className="underline decoration-dotted decoration-slate-300 group-hover:decoration-blue-400">{r.purchaseInvoiceDate}</span>
                <img src="/nts-logo.webp" alt="국세청" className="ml-1 h-5 w-auto inline-block align-middle" />
              </button>
            );
          }
          return <span className="text-slate-500">{r.purchaseInvoiceDate ?? '-'}</span>;
        },
        defaultOn: true,
      },
      {
        // 국세청 전자세금계산서 승인번호 — 같은 번호 = 같은 계산서 1장으로 묶인 매입.
        // 국세청 업로드(자동/수동 매칭) 시 기록됨. 클릭 → 묶음 모아보기 모달.
        key: 'ntsApprovalNo',
        label: '계산서번호',
        type: 'text',
        accessor: (r) => r.ntsApprovalNo ?? '',
        render: (r) => {
          const no = r.ntsApprovalNo;
          if (!no) return <span className="text-slate-300">-</span>;
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setNtsGroupNo(no); }}
              className="text-xs text-sky-700 underline decoration-dotted hover:text-sky-900 num"
              title={`국세청 승인번호 ${no}\n클릭하면 이 계산서로 묶인 매입을 모아 봅니다`}
            >
              …{no.slice(-8)}
            </button>
          );
        },
        defaultOn: true,
      },
      {
        key: 'actualOutDate',
        label: '출고일',
        type: 'date',
        accessor: (r) => r.actualOutDate ?? '',
        render: (r) => <span className="text-slate-500">{r.actualOutDate ?? '-'}</span>,
        defaultOn: true,
      },
      {
        key: 'salesTo',
        label: '엔드유저',
        type: 'text',
        // 출고 대상(actualSalesTo — 출고 승인 시 기록) 우선, 없으면 영업건/매입의 엔드유저.
        // 재고 매입(end_user='재고')이 유지보수 등으로 출고된 뒤에도 '재고'로 남아 보이던 문제 (2026-06-11).
        accessor: (r) => r.actualSalesTo ?? r.salesEndUser ?? '',
        render: (r) => {
          const label = r.actualSalesTo ?? r.salesEndUser;
          if (label) return <span className="text-xs cell-link">{label}{r.autoFlags?.salesTo && <AutoMark />}</span>;
          return <span className="text-slate-300">-</span>;
        },
        defaultOn: true,
      },
      {
        key: 'salesInvoiceDate',
        label: '매출 발행일',
        type: 'date',
        // 영업연동 매입의 매출(영업) 계산서 발행일. 영업건이 없는 유형은 항상 빈 칸.
        //   ⚠ 2026-09-12: 종전에는 '재고보유' 만 '-' 로 두어, 재고보유를 기타·판관비·제조로
        //   정리하는 순간 **팔지도 않은 줄에 주황색 「미발행」** 이 뜨는 거짓 경고가 생겼다.
        //   유지보수는 매출계산서(maintInvoiceDate)가 있을 수 있어 값이 있으면 그대로 보여준다.
        accessor: (r) => r.salesInvoiceDate ?? '',
        render: (r) => {
          const noSalesDeal =
            r.type === '재고보유' || r.type === '기타' || r.type === '판관비' || r.type === '제조' ||
            (r.type === '유지보수' && !r.salesInvoiceDate);
          if (noSalesDeal) {
            return <span className="text-slate-300" title="영업건에 연결되지 않은 매입 — 매출 계산서 개념이 없음">-</span>;
          }
          if (r.salesInvoiceDate) {
            return <span className="text-slate-600">{r.salesInvoiceDate}{r.autoFlags?.salesInvoiceDate && <AutoMark />}</span>;
          }
          return <span className="text-amber-600" title="영업건 매출 계산서 미발행">미발행</span>;
        },
        defaultOn: true,
      },
      {
        key: 'salesProjectName',
        label: '건명',
        type: 'text',
        accessor: (r) => r.salesProjectName ?? '',
        render: (r) => {
          const v = r.salesProjectName;
          if (!v) return <span className="text-slate-300">-</span>;
          // 글자수 제한 없이 칼럼 너비만큼 전체 표시(줄바꿈). 너비는 📐 내 표 설정에서 조절.
          // 영업건이 물려 있으면 눌러서 그 영업건 정보를 볼 수 있다 (보기 전용, v2.78.0).
          // ⚠ 행 클릭(매입 수정)과 겹치므로 전파 차단. 영업건이 없는 행은 종전대로 글씨만.
          if (!r.salesId) {
            return (
              <div className="whitespace-pre-wrap break-words text-xs text-slate-700" title={v}>
                {v}{r.autoFlags?.salesProjectName && <AutoMark />}
              </div>
            );
          }
          return (
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setSummarySalesId(r.salesId!);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setSummarySalesId(r.salesId!);
                }
              }}
              title={`${v}\n\n클릭 → 이 영업건 정보 보기`}
              className="whitespace-pre-wrap break-words text-xs text-slate-700 cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-blue-700"
            >
              {v}{r.autoFlags?.salesProjectName && <AutoMark />}
            </div>
          );
        },
        defaultOn: false,
      },
      {
        key: 'invoiceCompany',
        label: '매출 발행업체',
        type: 'text',
        accessor: (r) => r.invoiceCompany ?? '',
        render: (r) => {
          const v = r.invoiceCompany;
          if (!v) return <span className="text-slate-300">-</span>;
          return (
            <div className="max-w-[160px] truncate text-xs text-slate-700" title={v}>
              {v}{r.autoFlags?.invoiceCompany && <AutoMark />}
            </div>
          );
        },
        defaultOn: false,
      },
    );

    // 최근수정(updated_at) — 맨 오른쪽. 헤더 클릭으로 '최근 수정 순' 정렬.
    // 같은 날짜는 안정정렬이라 백엔드 updated_at 내림차순(최신 우선)이 유지됨.
    cols.push({
      key: 'lastUpdate',
      label: '최근수정',
      type: 'date',
      align: 'center',
      accessor: (r) => r.lastUpdate ?? '',
      render: (r) => <span className="text-slate-500 text-xs whitespace-nowrap">{r.lastUpdate ?? '-'}</span>,
      defaultOn: true,
    });

    return cols;
  // canEdit·openPoPdf·openEvidence — 「발주서」 칸(v2.287.0 · v2.293.0 아이콘 · v2.308.0 📎)이 쓴다(로그인 뒤에는 안 바뀐다)
  }, [canEdit, openPoPdf, openEvidence]);

  // viewer 권한이 볼 수 없는 컬럼 — 금액성·매입 발행일·계산서번호. 풀세트에서 제외.
  const VIEWER_HIDDEN_KEYS = useMemo(
    () => new Set<string>(['unitPrice', 'amount', 'purchaseInvoiceDate', 'ntsApprovalNo']),
    [],
  );
  const fullColumns = useMemo<PurchaseColumn[]>(
    () => (isViewer ? COLUMNS.filter((c) => !VIEWER_HIDDEN_KEYS.has(c.key)) : COLUMNS),
    [isViewer, COLUMNS, VIEWER_HIDDEN_KEYS],
  );
  const fullAllKeys = useMemo(() => fullColumns.map((c) => c.key), [fullColumns]);
  const fullDefaultKeys = useMemo(
    () => fullColumns.filter((c) => c.defaultOn).map((c) => c.key),
    [fullColumns],
  );

  // ExcelMode = 편집 모드 flag. visibility/order/widths 적용은 prefs 존재 여부로 결정 (ON/OFF 무관).
  const excelMode = !!prefsQ.data?.purchaseTabExcelMode;

  const activeColKeys = useMemo<Set<string>>(
    () =>
      resolveActiveColumns(
        prefsQ.data?.purchaseTabColumns,
        prefsQ.data?.purchaseTabColumnsKnown,
        fullAllKeys,
        fullDefaultKeys,
      ),
    [prefsQ.data, fullAllKeys, fullDefaultKeys],
  );

  const orderedKeys = useMemo<string[]>(
    () => resolveColumnOrder(prefsQ.data?.purchaseTabColumnOrder, fullAllKeys),
    [prefsQ.data, fullAllKeys],
  );

  const visibleColumns = useMemo<PurchaseColumn[]>(() => {
    const map = new Map(fullColumns.map((c) => [c.key, c]));
    return orderedKeys
      .filter((k) => activeColKeys.has(k))
      .map((k) => map.get(k))
      .filter((c): c is PurchaseColumn => !!c);
  }, [orderedKeys, activeColKeys, fullColumns]);

  const columnWidths = prefsQ.data?.purchaseTabColumnWidths;
  const hasPrefs =
    !!prefsQ.data &&
    (!!prefsQ.data.purchaseTabColumns?.length ||
      !!prefsQ.data.purchaseTabColumnOrder?.length ||
      !!prefsQ.data.purchaseTabColumnWidths ||
      !!prefsQ.data.purchaseTabExcelMode);

  function toggleColumn(key: string) {
    const next = new Set(activeColKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    patchPrefs.mutate({
      purchaseTabColumns: [...next],
      purchaseTabColumnsKnown: [...fullAllKeys],
    });
  }
  function handleReorder(fromKey: string, toKey: string, position: 'before' | 'after') {
    const filtered = orderedKeys.filter((k) => k !== fromKey);
    const idx = filtered.indexOf(toKey);
    if (idx < 0) return;
    filtered.splice(position === 'before' ? idx : idx + 1, 0, fromKey);
    patchPrefs.mutate({ purchaseTabColumnOrder: filtered });
  }
  function handleMoveLeft(key: string) {
    const visKeys = visibleColumns.map((c) => c.key);
    const idx = visKeys.indexOf(key);
    if (idx <= 0) return;
    const prev = visKeys[idx - 1];
    if (prev) handleReorder(key, prev, 'before');
  }
  function handleMoveRight(key: string) {
    const visKeys = visibleColumns.map((c) => c.key);
    const idx = visKeys.indexOf(key);
    if (idx < 0 || idx >= visKeys.length - 1) return;
    const next = visKeys[idx + 1];
    if (next) handleReorder(key, next, 'after');
  }
  function handleResize(key: string, widthPx: number) {
    const next = { ...(columnWidths ?? {}), [key]: widthPx };
    patchPrefs.mutate({ purchaseTabColumnWidths: next });
  }
  function handleExcelModeReset() {
    patchPrefs.mutate({
      purchaseTabExcelMode: false,
      purchaseTabColumns: null as unknown as undefined,
      purchaseTabColumnsKnown: null as unknown as undefined,
      purchaseTabColumnOrder: null as unknown as undefined,
      purchaseTabColumnWidths: null as unknown as undefined,
    });
  }

  const visibleRows = useMemo(
    () => applyColState(baseRows, fullColumns, colState),
    [baseRows, fullColumns, colState],
  );

  // ── 점진 렌더링 (느림/멈춤 방지) ──────────────────────────
  // visibleRows(필터·정렬 후 전체) 중 renderLimit 개만 실제 <tr> 로 그림.
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const [renderLimit, setRenderLimit] = useState(PURCHASE_INITIAL_RENDER);
  // 사용자가 필터/정렬/검색을 '실제로' 바꿀 때만 처음부터 다시 + 맨 위로.
  // visibleRows 참조로 판단하면 colState 가 매 렌더 새 객체라 거의 매번 초기화돼버림 →
  // 의도(필터/정렬 값)의 문자열 서명으로 판단(값이 같으면 배경 새로고침 등엔 반응 X).
  const sortSig = colState.sortStack.map((s) => `${s.key}:${s.dir}`).join(',');
  const filterSig = Object.keys(colState.filters)
    .sort()
    .map((k) => `${k}=${[...(colState.filters[k] ?? [])].sort().join('+')}`)
    .join(';');
  const listResetKey = [
    q,
    companyFilter ?? '',
    [...activeTypes].sort().join(','),
    yearArr ? [...yearArr].sort((a, b) => a - b).join(',') : '',
    reviewOnly ? '1' : '',
    hideZeroStock ? '1' : '',
    [...activeStages].sort().join(','),
    sortSig,
    filterSig,
  ].join('|');
  useEffect(() => {
    setRenderLimit(PURCHASE_INITIAL_RENDER);
    scrollBoxRef.current?.scrollTo({ top: 0 });
  }, [listResetKey]);
  const renderedRows = useMemo(
    () => visibleRows.slice(0, renderLimit),
    [visibleRows, renderLimit],
  );
  const hasMoreRows = renderLimit < visibleRows.length;
  // 바닥 근처로 스크롤하면 다음 묶음 추가 (rAF 쓰로틀로 setState 폭주 방지).
  const loadMoreScheduledRef = useRef(false);
  const handleListScroll = useCallback(() => {
    if (loadMoreScheduledRef.current) return;
    const el = scrollBoxRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < PURCHASE_LOAD_AHEAD_PX) {
      loadMoreScheduledRef.current = true;
      requestAnimationFrame(() => {
        loadMoreScheduledRef.current = false;
        setRenderLimit((n) => (n < visibleRows.length ? n + PURCHASE_RENDER_STEP : n));
      });
    }
  }, [visibleRows.length]);

  // 합계 — 전체(API 응답, baseRowsRaw) + 필터 후(visibleRows).
  // 수량(orderQty)은 viewer 도 노출, 금액(amount)은 viewer 마스킹.
  // 표 하단 sticky tfoot 과 부모(PurchaseListPage) 헤더 합계 둘 다 사용.
  const footerTotals = useMemo(() => {
    let allQty = 0, allAmt = 0;
    for (const r of baseRowsRaw) {
      allQty += r.orderQty || 0;
      allAmt += r.amount || 0;
    }
    let fQty = 0, fAmt = 0;
    for (const r of visibleRows) {
      fQty += r.orderQty || 0;
      fAmt += r.amount || 0;
    }
    return {
      all: { count: baseRowsRaw.length, qty: allQty, amt: allAmt },
      filtered: { count: visibleRows.length, qty: fQty, amt: fAmt },
    };
  }, [baseRowsRaw, visibleRows]);
  const hasFilter = footerTotals.all.count !== footerTotals.filtered.count;

  // 부모(PurchaseListPage) 헤더에 필터 후 합계 끌어올리기
  const { count: ftCount, qty: ftQty, amt: ftAmt } = footerTotals.filtered;
  useEffect(() => {
    onTotalsChange?.({ count: ftCount, qty: ftQty, amt: ftAmt });
  }, [ftCount, ftQty, ftAmt, onTotalsChange]);

  // 페이지 헤더 [📥 엑셀] 버튼이 호출할 클라이언트 측 exporter 등록.
  // 사용자 컬럼 토글·필터·정렬이 그대로 반영됨.
  useEffect(() => {
    // 운영 서식 (매입관리대장.xlsm → xlsx) 템플릿 기반 — 백엔드에서 채워 다운로드.
    // 필터·정렬된 가시 행(visibleRows) 그대로 전송, 컬럼 토글 무시 (템플릿 컬럼 사용).
    exporterRef.current = async () => {
      await purchaseApi.downloadPurchaseListExcel(visibleRows);
    };
    return () => {
      exporterRef.current = null;
    };
  }, [exporterRef, visibleRows]);

  // 검색바 자동완성 후보 — 매출번호 / 품목 / 매입처 / 건명(사용처) 추출.
  const searchSuggestions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of baseRows) {
      if (r.salesId) set.add(r.salesId);
      if (r.itemName) set.add(r.itemName);
      if (r.description) set.add(r.description);
      if (r.supplier) set.add(r.supplier);
      if (r.salesEndUser) set.add(r.salesEndUser);
      if (r.actualSalesTo) set.add(r.actualSalesTo);
    }
    return [...set];
  }, [baseRows]);

  // 인라인 삭제
  // Row.memo 효과를 위해 핸들러를 stable 하게 묶음.
  const handleOpenHistory = useCallback((r: PurchaseListRow) => {
    setHistoryTarget({
      itemCode: r.itemCode,
      itemName: r.itemName,
      displayName: r.itemName ?? r.description ?? `#${r.purchaseId}`,
      description: r.description,
    });
  }, []);
  const handleEdit = useCallback((r: PurchaseListRow) => {
    setEditingId(r.purchaseId);
  }, []);

  // ── 체크박스 다중 선택 + 일괄 삭제 ──────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // 데이터 갱신(삭제·재조회) 후 이미 사라진 행은 선택에서 자동 제거
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const existing = new Set(baseRowsRaw.map((r) => r.purchaseId));
      const next = new Set<number>();
      for (const id of prev) if (existing.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [baseRowsRaw]);

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(visibleRows.map((r) => r.purchaseId)));
  }, [visibleRows]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // 헤더 체크박스 상태 — 보이는(필터 후) 행 기준
  const visibleSelectedCount = useMemo(
    () => visibleRows.reduce((n, r) => n + (selectedIds.has(r.purchaseId) ? 1 : 0), 0),
    [visibleRows, selectedIds],
  );
  const allVisibleSelected = visibleRows.length > 0 && visibleSelectedCount === visibleRows.length;

  const bulkDeleteMut = useMutation({
    mutationFn: (ids: number[]) => purchaseApi.bulkDeletePurchases(ids),
    onSuccess: (res) => {
      setSelectedIds(new Set());
      syncAllData(qc);
      if (res.warnings.length > 0) {
        const lines = res.warnings
          .map((w) => `• ${w.itemName ?? w.itemCode} : 현재 재고 ${w.currentStock}`)
          .join('\n');
        alert(
          `⚠ 매입 삭제로 다음 품목의 재고가 0 미만이 됩니다. 확인 바랍니다.\n\n${lines}`,
        );
      }
    },
    onError: (e: Error) => alert(`일괄 삭제 실패: ${e.message}`),
  });
  const handleBulkDelete = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`선택한 매입 ${ids.length}건을 삭제하시겠습니까?\n삭제 후에는 변경 이력에서만 확인할 수 있습니다.`)) return;
    bulkDeleteMut.mutate(ids);
  }, [selectedIds, bulkDeleteMut]);

  return (
    <div>
      {/*
        등록 버튼 (상단) — 표 하단에도 같은 버튼이 있으나 발견 편의를 위해 상단에도 표시.
        왼쪽 = 🧾 국세청 마지막 업로드 (v2.239.0 · 전 직원) — ⚠두 줄(11px)이라 단추 높이 안에 들어간다.
          새 줄로 빼면 아래 표 높이(100vh − 320px)가 밀려 표 끝이 화면 밖으로 나간다.
      */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <NtsLastUpload />
      {(canEdit || canImport) && (
        <div className="flex justify-end gap-2 shrink-0">
          {canImport && (
            <button
              type="button"
              onClick={() => setCheckOpen(true)}
              className="btn btn-warning"
              title="국세청 업로드가 엉뚱한 매입에 잘못 붙였을 가능성이 있는 건(발행일 불일치 등)을 모아 점검"
            >
              🔍 매칭 점검
            </button>
          )}
          {canImport && (
            <button
              type="button"
              onClick={() => setNtsOpen(true)}
              className="btn btn-success"
              title="국세청 매입전자세금계산서 목록(.xls) 을 올려 발행일 자동 채움 + 누락분 확인필요 등록"
            >
              <img src="/nts-logo.webp" alt="" className="h-5 w-auto" />국세청 매입계산서 업로드
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="btn btn-primary"
            >
              + 매입 등록
            </button>
          )}
        </div>
      )}
      </div>
      {/* 필터 바 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mb-3">
        <SearchBar
          value={q}
          onCommit={setQ}
          placeholder="🔍 매출번호 / 품목 / 매입처 / 건명"
          className="mb-3"
          suggestions={searchSuggestions}
        />

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {/* 진행단계 다중 토글 — OR 합집합 */}
            <div className="flex gap-1 flex-wrap">
              {ALL_PURCHASE_STAGES.map((s) => {
                const on = activeStages.has(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStage(s)}
                    aria-pressed={on}
                    title={on ? '클릭하여 숨기기' : '클릭하여 표시'}
                    className={`px-3 py-1 rounded text-sm transition ${
                      on
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-400 line-through hover:bg-slate-200'
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            {/* 사업자 다중 토글 */}
            <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200">
              <span className="text-xs text-slate-500">사업자</span>
              <div className="flex gap-1">
                {ALL_COMPANIES.map((c) => {
                  const on = activeCompanies.has(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCompany(c)}
                      aria-pressed={on}
                      title={on ? '클릭하여 숨기기' : '클릭하여 표시'}
                      className={`px-2.5 py-1 rounded text-sm transition ${
                        on
                          ? 'bg-slate-700 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-400 line-through hover:bg-slate-200'
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* 유형 다중 토글 (사업자처럼 버튼) */}
            <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200">
              <span className="text-xs text-slate-500">유형</span>
              <div className="flex gap-1">
                {ALL_TYPES.map((t) => {
                  const on = activeTypes.has(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleType(t)}
                      aria-pressed={on}
                      title={on ? '클릭하여 숨기기' : '클릭하여 표시'}
                      className={`px-2.5 py-1 rounded text-sm transition ${
                        on
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-400 line-through hover:bg-slate-200'
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* 확인필요 토글 (국세청 자동등록 대기) */}
            <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200">
              <button
                type="button"
                onClick={() => setReviewOnly((v) => !v)}
                aria-pressed={reviewOnly}
                title="국세청·결의서에서 자동 등록되어 확인 대기 중인 매입만 보기 — 켜면 유형 필터와 상관없이 모두 보입니다(판관비가 꺼져 있어도)"
                className={`px-2.5 py-1 rounded text-sm transition ${
                  reviewOnly
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-500 hover:bg-amber-50'
                }`}
              >
                ⚠ 확인필요만
              </button>
            </div>
            {/* 📦 보유 0 숨기기 — 입출고가 끝난(보유 0) 줄을 감춘다. 미입고 발주 줄은 그대로. */}
            <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200">
              <button
                type="button"
                onClick={() => setHideZeroStock((v) => !v)}
                aria-pressed={hideZeroStock}
                title={
                  hideZeroStock
                    ? `보유가 0 인 줄 ${hiddenZeroCount.toLocaleString('ko-KR')}건을 숨기는 중 — 클릭하여 다시 표시`
                    : '입고 후 다 나가서 [보유] 가 0 인 줄(출고 완료·서비스)을 숨깁니다. 아직 입고 전인 발주 줄은 그대로 보입니다.'
                }
                className={`px-2.5 py-1 rounded text-sm transition ${
                  hideZeroStock
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-500 hover:bg-emerald-50'
                }`}
              >
                📦 보유 0 숨기기{hideZeroStock && hiddenZeroCount > 0 ? ` (${hiddenZeroCount.toLocaleString('ko-KR')})` : ''}
              </button>
            </div>
          </div>
          {/* 입고 연도 드롭다운 */}
          {availableYears.length > 0 && (
            <YearDropdown
              label="📅 입고 연도"
              availableYears={availableYears}
              selectedYears={selectedYears}
              onToggle={toggleYear}
              onSelectAll={() => setSelectedYears(new Set(availableYears))}
              onClearAll={() => setSelectedYears(new Set())}
              yearOn={(y) => selectedYears.has(y)}
              allYearsOn={selectedYears.size === availableYears.length}
              allYearsOff={selectedYears.size === 0}
            />
          )}
          {/* 우측 묶음 — [필터·정렬 초기화 ▼] [📐 내 표 설정 | ↺ 초기화] */}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <ResetMenu
              onResetDefault={resetToDefault}
              onResetAll={resetAllOff}
              defaultLabel="진행/사업자/유형 전체, 최근 2개년"
            />
            <ExcelModeGroup
              enabled={excelMode}
              hasPrefs={hasPrefs}
              userName={user?.name ?? '사용자'}
              onChange={(on) => patchPrefs.mutate({ purchaseTabExcelMode: on })}
              onReset={handleExcelModeReset}
            />
          </div>
        </div>

        {/* 상태 안내 — 미입고 표시 여부 */}
        {availableYears.length > 0 && (
          <div className="mt-2 text-xs">
            {selectedYears.size > 0 ? (
              <span className="text-slate-400">
                ※ 미입고 매입(입고일 비어있음)은 연도 필터에 잡히지 않음 — [모두 해제] 또는 연도 전체 해제 시 표시
              </span>
            ) : (
              <span className="text-emerald-600">✓ 모든 매입 표시 중 (미입고 포함)</span>
            )}
            <span className="text-slate-400 ml-3">
              {query.data ? `· 필터 후 ${visibleRows.length}/${query.data.page.total}건` : ''}
            </span>
          </div>
        )}
      </div>

      {/* 컬럼 표시 토글 — 📐 내 표 설정 ON 일 때만 노출. 모바일 hidden. */}
      {excelMode && (
        <div className="mb-2 text-xs relative hidden md:block">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setColMenuOpen(v => !v)}
              className="text-blue-600 hover:underline"
            >
              ⚙ 표시 컬럼 ({visibleColumns.length}/{fullAllKeys.length}{patchPrefs.isPending ? ' · 저장 중...' : ''})
              {colMenuOpen ? ' ▲' : ' ▼'}
            </button>
            <span className="text-xs text-slate-500">
              💡 컬럼 헤더를 끌면 순서 변경 · 우측 끝을 끌면 너비 조정 (자동 저장)
            </span>
          </div>
          {colMenuOpen && (
            <div className="absolute z-30 bg-white border border-slate-300 rounded shadow-md mt-1 p-3 grid grid-cols-3 gap-x-3 gap-y-1 min-w-[480px]">
              {fullColumns.map((c) => (
                <label key={c.key} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                  <input
                    type="checkbox"
                    checked={activeColKeys.has(c.key)}
                    onChange={() => toggleColumn(c.key)}
                  />
                  <span className="text-slate-700">{c.toggleLabel ?? c.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 선택 액션 바 — 체크박스 다중 선택 + 일괄 삭제 */}
      {canEdit && (
        <div className="mb-2 flex items-center gap-2 flex-wrap text-xs">
          <button
            type="button"
            onClick={selectAllVisible}
            disabled={visibleRows.length === 0 || allVisibleSelected}
            className="btn btn-secondary btn-sm"
            title="현재 필터로 보이는 행 전체를 선택"
          >
            ☑ 전체선택{visibleRows.length > 0 ? ` (${visibleRows.length}건)` : ''}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            disabled={selectedIds.size === 0}
            className="btn btn-secondary btn-sm"
          >
            ☐ 전체해제
          </button>
          {selectedIds.size > 0 && (
            <>
              <span className="text-blue-700 font-semibold">{selectedIds.size}건 선택됨</span>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={bulkDeleteMut.isPending}
                className="btn btn-danger btn-sm"
              >
                {bulkDeleteMut.isPending ? '삭제 중…' : `🗑 선택 삭제 (${selectedIds.size}건)`}
              </button>
            </>
          )}
        </div>
      )}

      {/* 표 — wrapper 가 scroll container 라 thead sticky 가 본문 스크롤 시 작동 */}
      <div
        ref={scrollBoxRef}
        onScroll={handleListScroll}
        className="bg-white rounded shadow-sm border border-slate-200 overflow-auto"
        style={{ maxHeight: 'calc(100vh - 320px)' }}
      >
        <table className="w-full text-[13px] border-collapse">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-20">
            <tr className="text-slate-700">
              {canEdit && (
                <th className="px-2 py-2 text-center w-10">
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el) el.indeterminate = visibleSelectedCount > 0 && !allVisibleSelected;
                    }}
                    checked={allVisibleSelected}
                    onChange={() => (allVisibleSelected ? clearSelection() : selectAllVisible())}
                    title={allVisibleSelected ? '전체 해제' : '보이는 행 전체 선택'}
                    className="cursor-pointer"
                  />
                </th>
              )}
              {visibleColumns.map((col, idx) => (
                <ColumnHeader
                  key={col.key}
                  column={col}
                  baseRows={baseRows}
                  // 연쇄 필터 — applyColState 와 같은 컬럼 집합
                  columns={fullColumns}
                  colState={colState}
                  excelMode={excelMode}
                  onReorder={excelMode ? handleReorder : undefined}
                  onMoveLeft={excelMode && idx > 0 ? () => handleMoveLeft(col.key) : undefined}
                  onMoveRight={
                    excelMode && idx < visibleColumns.length - 1
                      ? () => handleMoveRight(col.key)
                      : undefined
                  }
                  onResize={excelMode ? handleResize : undefined}
                  widthPx={columnWidths?.[col.key]}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && !query.data && (
              <tr>
                <td
                  colSpan={visibleColumns.length + (canEdit ? 1 : 0)}
                  className="text-center py-8 text-slate-400"
                ><LoadingLines /></td>
              </tr>
            )}
            {query.isError && (
              <tr>
                <td
                  colSpan={visibleColumns.length + (canEdit ? 1 : 0)}
                  className="text-center py-8 text-red-500"
                >
                  오류: {(query.error as Error).message}
                </td>
              </tr>
            )}
            {!query.isLoading && visibleRows.length === 0 && !query.isError && (
              <tr>
                <td
                  colSpan={visibleColumns.length + (canEdit ? 1 : 0)}
                  className="text-center py-8 text-slate-400"
                >
                  조건에 맞는 데이터가 없습니다.
                </td>
              </tr>
            )}
            {renderedRows.map((row) => (
              <Row
                key={row.purchaseId}
                row={row}
                columns={visibleColumns}
                canEdit={canEdit}
                selected={selectedIds.has(row.purchaseId)}
                onToggleSelect={handleToggleSelect}
                onOpenHistory={handleOpenHistory}
                onEdit={handleEdit}
              />
            ))}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300 z-10">
              <tr className="text-xs text-slate-700">
                <td
                  colSpan={visibleColumns.length + (canEdit ? 1 : 0)}
                  className="px-3 py-2"
                >
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="font-medium text-slate-800">
                      전체 {footerTotals.all.count}건 · 수량 {fmtKrw(footerTotals.all.qty)} · 금액 {isViewer ? '***' : `₩${fmtKrw(footerTotals.all.amt)}`}
                    </span>
                    {hasFilter && (
                      <span className="text-blue-700 font-medium">
                        필터 후 {footerTotals.filtered.count}건 · 수량 {fmtKrw(footerTotals.filtered.qty)} · 금액 {isViewer ? '***' : `₩${fmtKrw(footerTotals.filtered.amt)}`}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* 표 하단 */}
      {query.data && (
        <div className="mt-3 flex justify-between items-center">
          <div className="text-xs text-slate-500">
            {hasMoreRows ? (
              <>
                {renderedRows.length}건 표시 · 필터 후 {visibleRows.length}건
                <span className="text-slate-400"> — 아래로 스크롤하면 더 보기</span>
              </>
            ) : (
              <>
                {visibleRows.length}건 표시
                {visibleRows.length !== query.data.page.total &&
                  ` (전체 ${query.data.page.total}건 중)`}
              </>
            )}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="btn btn-primary"
            >
              + 매입 등록
            </button>
          )}
        </div>
      )}

      {/* 단가/금액 클릭 — 주요품목 원가와 동일한 '최근 매입 내역' 모달(최근/평균 매입가 배너 + lot 목록). lazy 라 처음 열 때 동적 로드. */}
      <Suspense fallback={null}>
        {historyTarget && (
          <PurchaseDetailModal
            itemCode={historyTarget.itemCode}
            itemName={historyTarget.itemName}
            displayName={historyTarget.displayName}
            rowDescription={historyTarget.description}
            variant="full"
            onClose={() => setHistoryTarget(null)}
          />
        )}
        {/* 등록 모달 */}
        {creating && (
          <PurchaseFormModal
            mode="create"
            onClose={() => setCreating(false)}
            onSaved={() => setCreating(false)}
          />
        )}
        {/* 국세청 매입계산서 업로드 모달 */}
        {ntsOpen && (
          <NtsImportModal
            onClose={() => setNtsOpen(false)}
            onApplied={() => { syncAllData(qc); }}
          />
        )}
        {/* 국세청 매칭 점검 — 의심 매칭 모아보기 */}
        {checkOpen && (
          <NtsCheckModal
            onClose={() => setCheckOpen(false)}
            onPurchaseClick={(pid) => { setCheckOpen(false); setEditingId(pid); }}
          />
        )}
        {/* 계산서 묶음 모아보기 — 계산서번호 칸 클릭 */}
        {ntsGroupNo && (
          <NtsGroupModal approvalNo={ntsGroupNo} onClose={() => setNtsGroupNo(null)} />
        )}
        {/* 📎 매입 증빙 여럿 — 고르는 창 (v2.308.0) */}
        {evidenceFor !== null && (
          <AttachmentDownloadModal
            parentKind="purchase"
            parentId={String(evidenceFor)}
            kind={PURCHASE_ATTACH_KIND}
            title="📎 매입 증빙"
            onClose={() => setEvidenceFor(null)}
          />
        )}
        {/* 영업건 요약 (보기 전용) — 건명·매출일련번호 칸 클릭 (v2.78.0) */}
        {summarySalesId && (
          <SalesSummaryModal salesId={summarySalesId} onClose={() => setSummarySalesId(null)} />
        )}
        {/* 수정 모달 — detail fetch 완료 후 마운트 (몇십ms 가량 짧은 지연) */}
        {editingId !== null && editingDetail && (
          <PurchaseFormModal
            mode="edit"
            initial={editingDetail}
            onClose={() => setEditingId(null)}
            onSaved={() => setEditingId(null)}
          />
        )}
      </Suspense>
    </div>
  );
}

// React.memo — 행 단위 메모. row/columns/canEdit/콜백 reference 가 같으면 재렌더 skip.
// 1000+ 행에서 다른 행 hover 등 사소한 부모 재렌더 시 모든 행 재렌더 방지.
//
// 클릭 영역 분리 (운영 정책):
//   · 단가/금액 셀 — 같은 품목 '최근 매입 내역' 모달 (주요품목 원가와 동일: 최근/평균 매입가 + lot 목록)
//   · 그 외 셀 — 매입 상세(수정) 모달
const HISTORY_TRIGGER_KEYS = new Set(['unitPrice', 'amount']);
const Row = memo(function Row({
  row,
  columns,
  canEdit,
  selected,
  onToggleSelect,
  onOpenHistory,
  onEdit,
}: {
  row: PurchaseListRow;
  columns: Column<PurchaseListRow>[];
  canEdit: boolean;
  selected: boolean;
  onToggleSelect: (id: number) => void;
  onOpenHistory: (r: PurchaseListRow) => void;
  onEdit: (r: PurchaseListRow) => void;
}) {
  return (
    <tr
      className={`border-b border-slate-100 ${selected ? ROW_BG_SELECTED : (ROW_BG_BY_TYPE[row.type] ?? ROW_BG_DEFAULT)}`}
    >
      {canEdit && (
        <td
          className="px-2 py-2 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(row.purchaseId)}
            className="cursor-pointer"
          />
        </td>
      )}
      {columns.map((col) => {
        const alignCls =
          col.align === 'right'
            ? 'text-right'
            : col.align === 'center'
            ? 'text-center'
            : 'text-left';
        const isHistoryTrigger = HISTORY_TRIGGER_KEYS.has(col.key);
        const onClick = isHistoryTrigger
          ? () => onOpenHistory(row)
          : canEdit
          ? () => onEdit(row)
          : undefined;
        return (
          <td
            key={col.key}
            className={`px-2 py-1 border-b border-slate-200 ${alignCls} ${onClick ? 'cursor-pointer' : ''}`}
            onClick={onClick}
            title={isHistoryTrigger ? '최근 매입 내역(단가·금액)을 조회합니다' : undefined}
          >
            {col.render ? col.render(row) : String(col.accessor(row) ?? '')}
          </td>
        );
      })}
    </tr>
  );
});

// ========================================================================
// 탭2 — 주요품목 원가 (Phase 7-1b)
// ========================================================================
// 좌·우 분할: HDD / 기타 (display_group === 'HDD' 기준).
// 각 행 클릭 → 같은 품목 최근 20건 매입 내역 모달 (variant='full').
// viewer 는 최근/평균 매입가 컬럼 hidden (백엔드도 null 마스킹).
function KeyCostTab() {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const [historyTarget, setHistoryTarget] = useState<KeyItemCostRow | null>(null);

  const query = useQuery({
    queryKey: ['purchases', 'key-item-costs'],
    queryFn: () => purchaseApi.listKeyItemCosts(),
    staleTime: 60_000,
  });

  const rows = query.data ?? [];
  const hddItems = rows.filter((r) => r.displayGroup === 'HDD');
  const otherItems = rows.filter((r) => r.displayGroup !== 'HDD');

  return (
    <>
      {/* 안내 박스 */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-xs text-blue-900 leading-relaxed">
        💡 본 표의 품목 목록은 <strong>재고관리 → 주요재고현황</strong> 과 동일하며, 같은 분류(HDD / 기타)로 구분.
        매입 데이터에서 최근/평균 매입가가 자동 계산됨 (PostgreSQL view <code>v_item_purchase_price</code>).
        {!isViewer && ' 매입가는 영업부 권한자만 표시.'}
      </div>

      {query.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
      )}
      {query.isError && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-red-500 text-sm">
          오류: {(query.error as Error).message}
        </div>
      )}

      {query.data && (
        <div className="grid grid-cols-2 gap-4">
          <KeyCostBlock
            title="🟦 HDD"
            items={hddItems}
            headerClass="bg-blue-50 text-blue-800"
            isViewer={isViewer}
            onPick={setHistoryTarget}
          />
          <KeyCostBlock
            title="⬜ 기타"
            items={otherItems}
            headerClass="bg-slate-100 text-slate-700"
            isViewer={isViewer}
            onPick={setHistoryTarget}
          />
        </div>
      )}

      {/* 행 클릭 — 같은 품목 최근 20건 (full 변형). lazy 모달이라 Suspense 필요 */}
      <Suspense fallback={null}>
        {historyTarget && (
          <PurchaseDetailModal
            itemCode={historyTarget.itemCode}
            itemName={historyTarget.itemName}
            displayName={historyTarget.itemName ?? historyTarget.itemCode}
            variant="full"
            recentUnitPrice={historyTarget.recentUnitPrice}
            recentInDate={historyTarget.recentInDate}
            avgUnitPrice={historyTarget.avgUnitPrice}
            onClose={() => setHistoryTarget(null)}
          />
        )}
      </Suspense>
    </>
  );
}

function KeyCostBlock({
  title,
  items,
  headerClass,
  isViewer,
  onPick,
}: {
  title: string;
  items: KeyItemCostRow[];
  headerClass: string;
  isViewer: boolean;
  onPick: (item: KeyItemCostRow) => void;
}) {
  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 overflow-hidden">
      <div
        className={`px-4 py-2.5 ${headerClass} border-b border-slate-200 font-semibold text-sm flex items-center justify-between`}
      >
        <span>{title}</span>
        <span className="text-xs font-normal opacity-80">{items.length}종</span>
      </div>
      <div className="overflow-x-auto">
        {/* mockup data-table 정합 — index.css 의 .data-table 정의 사용
            (가로줄 1px slate-200, 패딩 10px 12px, 헤더 회색 배경) */}
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-left">품목</th>
              {!isViewer && (
                <>
                  <th className="text-right">최근 매입가</th>
                  <th className="text-left">최근 매입일</th>
                  <th className="text-right">평균 매입가</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={isViewer ? 1 : 4}
                  className="text-center text-slate-500 text-xs"
                >
                  품목이 없습니다.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr
                key={item.itemCode}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => onPick(item)}
              >
                <td className="font-medium">{item.itemName ?? item.itemCode}</td>
                {!isViewer && (
                  <>
                    <td className="text-right num">
                      {item.recentUnitPrice === null
                        ? <span className="text-slate-300">—</span>
                        : item.recentUnitPrice.toLocaleString('ko-KR')}
                    </td>
                    <td className="text-slate-500 text-xs">
                      {item.recentInDate ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="text-right num text-slate-700">
                      {item.avgUnitPrice === null
                        ? <span className="text-slate-300">—</span>
                        : item.avgUnitPrice.toLocaleString('ko-KR')}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========================================================================
// 탭3 — 연도별 매입/출고 (Phase 7-1c)
// ========================================================================
// HDD / 기타 토글 + 그룹별 rowspan + 매입수량(3년) + 출고수량(3년) + 당년 매입금액 + 전년대비
function YearlyTab({ year }: { year: number }) {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const [cat, setCat] = useState<'HDD' | '기타'>('HDD');

  const query = useQuery({
    queryKey: ['purchases', 'yearly-stats', year],
    queryFn: () => purchaseApi.getYearlyStats(year),
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
    );
  }
  if (query.isError) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-red-500 text-sm">
        오류: {(query.error as Error).message}
      </div>
    );
  }
  if (!query.data) return null;

  const data = query.data;
  const items =
    cat === 'HDD'
      ? data.items.filter((i) => i.displayGroup === 'HDD')
      : data.items.filter((i) => i.displayGroup !== 'HDD');

  const hddCount = data.items.filter((i) => i.displayGroup === 'HDD').length;
  const otherCount = data.items.length - hddCount;

  // item_group 으로 묶어 rowspan 계산 (재고관리 동일 패턴)
  const groupMap = new Map<string, PurchaseYearlyItem[]>();
  for (const it of items) {
    const key = it.itemGroup ?? '(미분류)';
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(it);
  }

  const sumYear = (key: 'qty' | 'amt' | 'out', y: number): number =>
    items.reduce((s, it) => {
      const v = it.stats[String(y)]?.[key];
      return s + (typeof v === 'number' ? v : 0);
    }, 0);

  const curAmt = sumYear('amt', data.year);
  const prevAmt = sumYear('amt', data.year - 1);
  const pctTotal =
    prevAmt === 0 ? '-' : `${(((curAmt - prevAmt) / prevAmt) * 100).toFixed(1)}%`;
  const pctClsTotal =
    prevAmt === 0
      ? 'text-slate-400'
      : curAmt >= prevAmt
        ? 'text-red-600'
        : 'text-blue-600';

  return (
    <>
      {/* 토글 — 품목군 (숫자 = 종 수) */}
      <div className="mb-3">
        <SegmentedControl
          ariaLabel="품목군"
          options={[
            { key: 'HDD', label: 'HDD', count: hddCount },
            { key: '기타', label: '기타', count: otherCount },
          ]}
          value={cat}
          onChange={setCat}
        />
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50">
              <th rowSpan={2} className="text-left">
                품목그룹
              </th>
              <th rowSpan={2} className="text-left">
                품목
              </th>
              <th colSpan={3} className="text-center bg-blue-50 text-blue-800 border-l border-r border-slate-200">
                매입 수량
              </th>
              <th colSpan={3} className="text-center bg-emerald-50 text-emerald-800 border-r border-slate-200">
                출고 수량
              </th>
              {!isViewer && (
                <th colSpan={2} className="text-center bg-amber-50 text-amber-800">
                  매입 금액 ({data.year} 기준)
                </th>
              )}
            </tr>
            <tr>
              {data.years.map((y) => (
                <th
                  key={`in-${y}`}
                  className={`text-right ${
                    y === data.year ? 'bg-blue-100 text-blue-900' : ''
                  }`}
                >
                  {y}
                  {y === data.year ? ' ★' : ''}
                </th>
              ))}
              {data.years.map((y) => (
                <th
                  key={`out-${y}`}
                  className={`text-right ${
                    y === data.year ? 'bg-emerald-100 text-emerald-900' : ''
                  }`}
                >
                  {y}
                  {y === data.year ? ' ★' : ''}
                </th>
              ))}
              {!isViewer && (
                <>
                  <th className="text-right">{data.year} 매입금액</th>
                  <th className="text-right">전년대비</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={isViewer ? 8 : 10}
                  className="text-center text-slate-500 text-xs"
                >
                  품목이 없습니다.
                </td>
              </tr>
            )}
            {Array.from(groupMap.entries()).map(([grp, list]) =>
              list.map((it, i) => {
                const cur = it.stats[String(data.year)]?.amt;
                const prev = it.stats[String(data.year - 1)]?.amt;
                const pct =
                  cur === null || prev === null || prev === undefined || cur === undefined
                    ? '-'
                    : prev === 0
                      ? '-'
                      : `${(((cur - prev) / prev) * 100).toFixed(1)}%`;
                const pctCls =
                  cur === null || prev === null || prev === 0 || prev === undefined
                    ? 'text-slate-400'
                    : cur! >= prev
                      ? 'text-red-600'
                      : 'text-blue-600';
                return (
                  <tr key={it.itemCode}>
                    {i === 0 && (
                      <td
                        rowSpan={list.length}
                        className="font-semibold bg-slate-50 align-middle text-slate-700"
                      >
                        {grp}
                      </td>
                    )}
                    <td className="font-medium">{it.itemName ?? it.itemCode}</td>
                    {data.years.map((y) => (
                      <td
                        key={`in-${y}`}
                        className={`text-right num ${
                          y === data.year
                            ? 'font-semibold bg-blue-50'
                            : 'text-slate-500'
                        }`}
                      >
                        {fmtKrw(it.stats[String(y)]?.qty ?? 0)}
                      </td>
                    ))}
                    {data.years.map((y) => (
                      <td
                        key={`out-${y}`}
                        className={`text-right num ${
                          y === data.year
                            ? 'font-semibold bg-emerald-50'
                            : 'text-slate-500'
                        }`}
                      >
                        {fmtKrw(it.stats[String(y)]?.out ?? 0)}
                      </td>
                    ))}
                    {!isViewer && (
                      <>
                        <td className="text-right num">
                          {cur === null || cur === undefined
                            ? '-'
                            : fmtKrw(cur)}
                        </td>
                        <td className={`text-right num ${pctCls}`}>{pct}</td>
                      </>
                    )}
                  </tr>
                );
              }),
            )}
            {items.length > 0 && (
              <tr className="bg-slate-100 font-bold">
                <td colSpan={2}>소계 ({items.length}품목)</td>
                {data.years.map((y) => (
                  <td
                    key={`sum-in-${y}`}
                    className={`text-right num ${
                      y === data.year ? 'bg-blue-100' : ''
                    }`}
                  >
                    {fmtKrw(sumYear('qty', y))}
                  </td>
                ))}
                {data.years.map((y) => (
                  <td
                    key={`sum-out-${y}`}
                    className={`text-right num ${
                      y === data.year ? 'bg-emerald-100' : ''
                    }`}
                  >
                    {fmtKrw(sumYear('out', y))}
                  </td>
                ))}
                {!isViewer && (
                  <>
                    <td className="text-right num">{fmtKrw(curAmt)}</td>
                    <td className={`text-right num ${pctClsTotal}`}>{pctTotal}</td>
                  </>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2">
        ※ 주요재고현황(is_key_stock=true) 품목 기준. 매입 수량/금액은 발주일자, 출고 수량은 실출고일 기준.
        전년대비는 {data.year} 매입금액과 {data.year - 1} 매입금액 비교.
      </p>
    </>
  );
}

// ========================================================================
// 탭4 — 주요 매입처 현황 (Phase 7-1c)
// ========================================================================
// sales+ 만 (도메인 규칙 — 매입금액). viewer 는 안내 화면.
// Top 5 매입처 × 3년 × 분기·합계, 분기별 펼치기 토글
function SupplierTab({ year, isViewer }: { year: number; isViewer: boolean }) {
  const [showQuarter, setShowQuarter] = useState(true);

  if (isViewer) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded p-8 text-center text-sm text-amber-900">
        🔒 주요 매입처 현황은 매입 금액 정보를 포함하므로 영업부 권한자만 열람할 수 있습니다.
      </div>
    );
  }

  const query = useQuery({
    queryKey: ['purchases', 'supplier-quarterly', year],
    queryFn: () => purchaseApi.getSupplierQuarterly(year, 5),
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
    );
  }
  if (query.isError) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-red-500 text-sm">
        오류: {(query.error as Error).message}
      </div>
    );
  }
  if (!query.data) return null;

  const data = query.data;
  const fmtMan = (n: number): string =>
    Math.round((n || 0) / 10000).toLocaleString('ko-KR') + '만';

  const isCurrent = (y: number) => y === data.year;
  const colSpanByYear = showQuarter ? 5 : 1;

  const pct = (cur: number, prev: number): string =>
    prev === 0 ? '-' : `${(((cur - prev) / prev) * 100).toFixed(1)}%`;
  const pctCls = (cur: number, prev: number) =>
    prev === 0
      ? 'text-slate-400'
      : cur >= prev
        ? 'text-red-600'
        : 'text-blue-600';

  return (
    <>
      <div className="flex items-center gap-3 mb-3 text-sm">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={showQuarter}
            onChange={(e) => setShowQuarter(e.target.checked)}
            className="rounded"
          />
          <span className="text-slate-600">분기별 펼치기</span>
        </label>
        <span className="text-slate-400">|</span>
        <span className="text-slate-500">
          상위 {data.topN}개 매입처 · 최근 3년 · {data.year}년 강조
        </span>
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th rowSpan={2} className="text-center bg-slate-100 w-12">
                순위
              </th>
              <th rowSpan={2} className="text-left bg-slate-100">
                매입처
              </th>
              {data.years.map((y) => (
                <th
                  key={`yh-${y}`}
                  colSpan={colSpanByYear}
                  className={`text-center border-l border-slate-200 ${
                    isCurrent(y)
                      ? 'bg-blue-600 text-white text-base'
                      : 'bg-violet-100 text-violet-800'
                  }`}
                >
                  {y}년{isCurrent(y) ? ' ★' : ''}
                </th>
              ))}
              <th
                rowSpan={2}
                className="text-right bg-amber-100 text-amber-800 border-l border-slate-200"
              >
                전년대비
                <br />({data.year})
              </th>
            </tr>
            <tr>
              {data.years.flatMap((y) => {
                const cur = isCurrent(y);
                if (!showQuarter) {
                  const tCls = cur
                    ? 'bg-blue-100 text-blue-900 font-bold'
                    : 'bg-emerald-50 text-emerald-700';
                  return [
                    <th key={`th-${y}-t`} className={`text-right ${tCls}`}>
                      합계
                    </th>,
                  ];
                }
                const qCls = cur ? 'bg-blue-50 text-blue-800' : '';
                const tCls = cur
                  ? 'bg-blue-100 text-blue-900 font-bold'
                  : 'bg-emerald-50 text-emerald-700';
                return [
                  <th key={`th-${y}-1`} className={`text-right ${qCls}`}>
                    1Q
                  </th>,
                  <th key={`th-${y}-2`} className={`text-right ${qCls}`}>
                    2Q
                  </th>,
                  <th key={`th-${y}-3`} className={`text-right ${qCls}`}>
                    3Q
                  </th>,
                  <th key={`th-${y}-4`} className={`text-right ${qCls}`}>
                    4Q
                  </th>,
                  <th key={`th-${y}-t`} className={`text-right ${tCls}`}>
                    합계
                  </th>,
                ];
              })}
            </tr>
          </thead>
          <tbody>
            {data.suppliers.length === 0 && (
              <tr>
                <td
                  colSpan={2 + data.years.length * colSpanByYear + 1}
                  className="text-center text-slate-500 text-xs"
                >
                  데이터가 없습니다.
                </td>
              </tr>
            )}
            {data.suppliers.map((s, idx) => (
              <SupplierRow
                key={s.name}
                supplier={s}
                rank={idx + 1}
                years={data.years}
                showQuarter={showQuarter}
                currentYear={data.year}
                fmtMan={fmtMan}
                pct={pct}
                pctCls={pctCls}
              />
            ))}
            {data.suppliers.length > 0 && (
              <tr className="bg-slate-100 font-bold">
                <td colSpan={2}>{data.suppliers.length}개사 합계</td>
                {data.years.flatMap((y) => {
                  const cur = isCurrent(y);
                  const tCls = cur ? 'bg-blue-200 text-blue-900' : 'bg-emerald-100';
                  if (!showQuarter) {
                    return [
                      <td
                        key={`gt-${y}-t`}
                        className={`text-right num ${tCls}`}
                      >
                        {fmtMan(data.grandTotal[String(y)] ?? 0)}
                      </td>,
                    ];
                  }
                  return [
                    <td
                      key={`gt-${y}-q`}
                      colSpan={4}
                      className="text-right num text-slate-400 italic"
                    >
                      — 분기 합산 —
                    </td>,
                    <td
                      key={`gt-${y}-t`}
                      className={`text-right num ${tCls}`}
                    >
                      {fmtMan(data.grandTotal[String(y)] ?? 0)}
                    </td>,
                  ];
                })}
                <td
                  className={`text-right num bg-amber-100 ${pctCls(
                    data.grandTotal[String(data.year)] ?? 0,
                    data.grandTotal[String(data.year - 1)] ?? 0,
                  )}`}
                >
                  {pct(
                    data.grandTotal[String(data.year)] ?? 0,
                    data.grandTotal[String(data.year - 1)] ?? 0,
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2">
        ※ 단위: 만원. 발주일자 기준 분기 분리. 상위 {data.topN}개 매입처만 집계.
      </p>
    </>
  );
}

function SupplierRow({
  supplier,
  rank,
  years,
  showQuarter,
  currentYear,
  fmtMan,
  pct,
  pctCls,
}: {
  supplier: PurchaseSupplierRow;
  rank: number;
  years: number[];
  showQuarter: boolean;
  currentYear: number;
  fmtMan: (n: number) => string;
  pct: (cur: number, prev: number) => string;
  pctCls: (cur: number, prev: number) => string;
}) {
  const rankCls =
    rank === 1
      ? 'bg-amber-100 text-amber-800'
      : rank === 2
        ? 'bg-slate-200 text-slate-700'
        : rank === 3
          ? 'bg-orange-100 text-orange-700'
          : 'bg-slate-50 text-slate-600';
  const last = supplier.stats[String(currentYear)]?.total ?? 0;
  const prev = supplier.stats[String(currentYear - 1)]?.total ?? 0;
  return (
    <tr className="hover:bg-slate-50">
      <td className="text-center">
        <span
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${rankCls}`}
        >
          {rank}
        </span>
      </td>
      <td className="font-medium">{supplier.name}</td>
      {years.flatMap((y) => {
        const yo = supplier.stats[String(y)] ?? { q1: 0, q2: 0, q3: 0, q4: 0, total: 0 };
        const cur = y === currentYear;
        if (!showQuarter) {
          const tCls = cur
            ? 'bg-blue-100 font-bold text-blue-900 text-base'
            : 'bg-emerald-50 font-semibold';
          return [
            <td key={`${y}-t`} className={`text-right num ${tCls}`}>
              {yo.total ? fmtMan(yo.total) : '·'}
            </td>,
          ];
        }
        const qCls = cur ? 'bg-blue-50 text-blue-700' : 'text-slate-500';
        const tCls = cur
          ? 'bg-blue-100 font-bold text-blue-900'
          : 'bg-emerald-50 font-semibold';
        return [
          <td key={`${y}-1`} className={`text-right num ${qCls}`}>
            {yo.q1 ? fmtMan(yo.q1) : '·'}
          </td>,
          <td key={`${y}-2`} className={`text-right num ${qCls}`}>
            {yo.q2 ? fmtMan(yo.q2) : '·'}
          </td>,
          <td key={`${y}-3`} className={`text-right num ${qCls}`}>
            {yo.q3 ? fmtMan(yo.q3) : '·'}
          </td>,
          <td key={`${y}-4`} className={`text-right num ${qCls}`}>
            {yo.q4 ? fmtMan(yo.q4) : '·'}
          </td>,
          <td key={`${y}-t`} className={`text-right num ${tCls}`}>
            {yo.total ? fmtMan(yo.total) : '·'}
          </td>,
        ];
      })}
      <td className={`text-right num bg-amber-50 ${pctCls(last, prev)}`}>
        {pct(last, prev)}
      </td>
    </tr>
  );
}
